import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
  /**
   * Disables the confirm button without showing the loading spinner. Use when
   * confirming is blocked on a precondition that is neither an error nor an
   * in-flight request (e.g. an async lookup the outcome depends on is still
   * resolving). Cancel stays enabled.
   */
  confirmDisabled?: boolean;
  onConfirm?: () => void | null | Promise<void>;
  onCancel: () => void;
  tertiaryLabel?: string;
  onTertiary?: () => void;
  tertiaryLoading?: boolean;
  tertiaryVariant?: 'primary' | 'ghost' | 'danger';
}

export function ConfirmDialog({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel,
  variant = 'primary',
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  tertiaryLabel,
  onTertiary,
  tertiaryLoading = false,
  tertiaryVariant = 'primary',
}: ConfirmDialogProps) {
  const resolvedCancelLabel = cancelLabel ?? (onConfirm === undefined ? 'Close' : 'Cancel');
  const anyLoading = loading || tertiaryLoading;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pressedRef = useRef<HTMLButtonElement | null>(null);

  // Focus Cancel on open, and hand focus back to whatever opened the dialog
  // (a menu trigger, a row button) when it closes - unless something else,
  // such as a dialog that replaced this one, has already taken it. Declared
  // before `useFocusTrap` so it reads the opener before the trap moves focus.
  // An opener that was unmounted and re-rendered meanwhile (a list row removed
  // optimistically, then restored) is found again by its `id`, so a trigger
  // inside such a row needs a stable `id` to get focus back.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    const openerId = opener instanceof HTMLElement ? opener.id : '';
    cancelRef.current?.focus();
    return () => {
      const focusIsLost = document.activeElement === null || document.activeElement === document.body;
      if (!focusIsLost) return;
      const target = opener instanceof HTMLElement && opener.isConnected
        ? opener
        : openerId ? document.getElementById(openerId) : null;
      target?.focus({ preventScroll: true });
    };
  }, [open]);
  const trapRef = useFocusTrap(open);

  // Loading disables the button the user just pressed, which drops focus out
  // of the dialog. When loading ends with the dialog still open (a refusal
  // shown inline), put focus back on that button, or on Cancel if it is gone.
  useEffect(() => {
    if (!open || anyLoading) return;
    const panel = trapRef.current;
    const pressed = pressedRef.current;
    if (!panel || panel.contains(document.activeElement)) return;
    if (pressed && panel.contains(pressed) && !pressed.disabled) {
      pressed.focus();
    } else {
      cancelRef.current?.focus();
    }
  }, [open, anyLoading, trapRef]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !anyLoading) {
        onCancel();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, anyLoading, onCancel]);

  if (!open) return null;

  // `m-0`: see FormDialog - an un-portalled `fixed inset-0` root picks up a
  // `space-y-*` parent's margin and stops short of the viewport.
  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 m-0 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={anyLoading ? undefined : onCancel}
      />
      {/* `text-left` is load-bearing, not decorative. This panel is `fixed`,
          which takes it out of layout flow but NOT out of style inheritance, and
          the component does not portal - so it renders wherever it was mounted and
          inherits that ancestor's `text-align`. Mounted inside a right-aligned
          table cell (a per-row action button), every line of the dialog came out
          right-aligned. A dialog is a top-level surface and owns its own
          alignment. */}
      <div ref={trapRef} className="animate-dialog-in relative w-full max-w-md rounded-xl border border-border bg-bg-primary p-4 text-left shadow-lg sm:p-6">
        <h2 id="confirm-dialog-title" className="font-serif text-lg font-semibold text-secondary">
          {title}
        </h2>
        <div id="confirm-dialog-message" className="mt-2 text-sm text-text-muted">
          {children ?? <p>{message}</p>}
        </div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={anyLoading}
          >
            {resolvedCancelLabel}
          </Button>
          {tertiaryLabel !== undefined && (
            <Button
              type="button"
              variant={tertiaryVariant}
              onClick={(event) => {
                pressedRef.current = event.currentTarget;
                onTertiary?.();
              }}
              loading={tertiaryLoading}
              disabled={anyLoading}
            >
              {tertiaryLabel}
            </Button>
          )}
          {onConfirm !== undefined && (
            <Button
              type="button"
              variant={variant === 'danger' ? 'danger' : 'primary'}
              onClick={(event) => {
                pressedRef.current = event.currentTarget;
                void onConfirm?.();
              }}
              loading={loading}
              disabled={anyLoading || confirmDisabled}
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
